import { useState, useEffect } from "react";
import styled from "styled-components";
import { Flex, Form, Input, Select, Button, Modal, message } from "antd";
import { LoadingOutlined } from "@ant-design/icons";
import FileSaver from "file-saver";

import { Slide, patchSlideData, downloadSlide } from "../../api/slideAPI";
import { Photo, GrossImage } from "../../api/postAPI";
import { checkUser } from "../../api/authAPI";
import { EditFilled } from "@ant-design/icons";

interface Props {
  currentSlide: Slide | null;
  onSlideUpdate: () => void | Promise<void>;
  isSlideFile: boolean;
  currentImage: Photo | null;
  isGrossFile?: boolean;
  currentGrossImage?: GrossImage | null;
  hasEditAuth: boolean;
  onEditImage: () => void;
  onEditGrossImage?: () => void;
  layout?: "row" | "col";
}

const StRow = ({ label, value }: { label: string; value: any }) => {
  if (!value) return null;

  return (
    <div style={{ display: "contents" }}>
      <StInfoLabel>{label}</StInfoLabel>
      <StInfoValue>{value}</StInfoValue>
    </div>
  );
};

const SlideInfoPanel = ({
  currentSlide,
  onSlideUpdate,
  isSlideFile,
  currentImage,
  isGrossFile = false,
  currentGrossImage,
  hasEditAuth,
  onEditImage,
  onEditGrossImage,
  layout = "row",
}: Props) => {
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDownloadBtn, setShowDownloadBtn] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [form] = Form.useForm<any>();

  useEffect(() => {
    checkUser().then((role) => {
      setShowDownloadBtn(
        role === "ADMIN_MAIN" || role === "ADMIN_SUB" || role === "ADVISOR"
      );
    });
  }, []);

  const editSlideInformation = async (values: any) => {
    if (!currentSlide?.slideId) return;
    setIsSubmitting(true);
    try {
      values.slideId = currentSlide.slideId;
      await patchSlideData(values);
      await onSlideUpdate();
      message.success("슬라이드 정보가 수정되었습니다.");
      setShowEditModal(false);
    } catch (e) {
      console.error(e);
      message.error("슬라이드 정보 수정에 실패했습니다.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const cancelEdit = () => {
    form.resetFields();
    setShowEditModal(false);
  };

  const handleDownload = async () => {
    if (!currentSlide) return;
    setIsDownloading(true);
    const fileData = await downloadSlide(currentSlide.slideId);
    if (fileData === false) {
      setIsDownloading(false);
      window.alert("다운로드 권한이 없습니다.");
      return;
    }
    FileSaver.saveAs(fileData, currentSlide.filename);
    setIsDownloading(false);
  };

  const hasInfo = isSlideFile
    ? !!(
        currentSlide?.slidename ||
        currentSlide?.caption ||
        currentSlide?.gender ||
        currentSlide?.age ||
        currentSlide?.site ||
        currentSlide?.zindanName
      )
    : isGrossFile
    ? !!(
        currentGrossImage?.caption ||
        currentGrossImage?.gender ||
        currentGrossImage?.age ||
        currentGrossImage?.site ||
        currentGrossImage?.zindanName ||
        currentGrossImage?.description
      )
    : !!(
        currentImage?.caption ||
        currentImage?.gender ||
        currentImage?.age ||
        currentImage?.site ||
        currentImage?.zindanName
      );

  return (
    <>
      <InfoPanelSubTitleWrapper>
        <InfoPanelSubTitle>
          {isSlideFile
            ? "Slide data"
            : isGrossFile
            ? "Gross Image data"
            : "Image data"}
        </InfoPanelSubTitle>
        <StEditButtonRow>
          {isSlideFile && hasEditAuth ? (
            <StEditButton
              type="default"
              size="small"
              style={{ marginTop: "5px" }}
              onClick={() => {
                form.setFieldsValue(currentSlide ?? {});
                setShowEditModal(true);
              }}
              icon={<EditFilled />}
            >
              Edit slide data
            </StEditButton>
          ) : isGrossFile && hasEditAuth ? (
            <StEditButton
              type="default"
              size="small"
              onClick={onEditGrossImage}
              icon={<EditFilled />}
            >
              Edit gross image data
            </StEditButton>
          ) : (
            hasEditAuth && (
              <StEditButton
                type="default"
                size="small"
                onClick={onEditImage}
                icon={<EditFilled />}
              >
                Edit image data
              </StEditButton>
            )
          )}
        </StEditButtonRow>
      </InfoPanelSubTitleWrapper>

      {hasInfo ? (
        <StSectionContent
          style={{
            display: "grid",
            gridTemplateColumns:
              layout === "col"
                ? "8rem minmax(0, 1fr) 8rem minmax(0, 1fr)"
                : "8rem minmax(0, 1fr)",
            rowGap: "10px",
            alignContent: "start",
          }}
        >
          {isSlideFile ? (
            <>
              <StRow label="Slide Name" value={currentSlide?.slidename} />
              <StRow label="Caption" value={currentSlide?.caption} />
              <StRow label="Gender" value={currentSlide?.gender} />
              <StRow label="Age" value={currentSlide?.age} />
              <StRow label="Site" value={currentSlide?.site} />
              <StRow label="Diagnosis" value={currentSlide?.zindanName} />
            </>
          ) : isGrossFile ? (
            <>
              <StRow label="Image Name" value={currentGrossImage?.caption} />
              <StRow label="Gender" value={currentGrossImage?.gender} />
              <StRow label="Age" value={currentGrossImage?.age} />
              <StRow label="Site" value={currentGrossImage?.site} />
              <StRow label="Diagnosis" value={currentGrossImage?.zindanName} />
              <StRow label="Description" value={currentGrossImage?.description} />
            </>
          ) : (
            <>
              <StRow label="Image Name" value={currentImage?.imageName} />
              <StRow label="Caption" value={currentImage?.caption} />
              <StRow label="Gender" value={currentImage?.gender} />
              <StRow label="Age" value={currentImage?.age} />
              <StRow label="Site" value={currentImage?.site} />
              <StRow label="Diagnosis" value={currentImage?.zindanName} />
            </>
          )}
        </StSectionContent>
      ) : (
        <EmptyDataMessage>
          {isSlideFile
            ? "No slide data available. Click Edit to add information."
            : isGrossFile
            ? "No gross image data available. Click Edit to add information."
            : "No image data available. Click Edit to add information."}
        </EmptyDataMessage>
      )}

      <Modal
        centered
        title="Edit slide data"
        open={showEditModal}
        onCancel={cancelEdit}
        footer={null}
        destroyOnClose
      >
        <Flex gap="middle" vertical>
          <Form
            form={form}
            onFinish={editSlideInformation}
            initialValues={currentSlide ?? {}}
            layout="vertical"
          >
            <Form.Item name="slidename" label="Slide Name">
              <Input />
            </Form.Item>
            <Form.Item name="caption" label="Caption">
              <Input />
            </Form.Item>
            <Form.Item name="gender" label="Gender">
              <Select>
                <Select.Option value="M">Male</Select.Option>
                <Select.Option value="F">Female</Select.Option>
                <Select.Option value="U">Unknown</Select.Option>
              </Select>
            </Form.Item>
            <Form.Item name="age" label="Age">
              <Input />
            </Form.Item>
            <Form.Item name="site" label="Site">
              <Input />
            </Form.Item>
            <Form.Item name="zindanName" label="Diagnosis">
              <Input />
            </Form.Item>
            <Form.Item style={{ marginTop: "20px" }}>
              <div style={{ display: "flex", justifyContent: "flex-end" }}>
                <Button onClick={cancelEdit} style={{ marginRight: "10px" }} disabled={isSubmitting}>
                  Cancel
                </Button>
                <Button type="primary" htmlType="submit" loading={isSubmitting}>
                  Submit
                </Button>
              </div>
            </Form.Item>
          </Form>
        </Flex>
      </Modal>

      {isDownloading && (
        <Modal open={isDownloading} centered closable={false} footer={null}>
          Downloading... <LoadingOutlined />
        </Modal>
      )}
    </>
  );
};

const StSectionContent = styled.div`
  padding: 10px;
  border-bottom: 1px solid lightgray;
  overflow-y: auto;

  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-track {
    background: #f1f1f1;
  }

  &::-webkit-scrollbar-thumb {
    background: #888;
    border-radius: 3px;
  }

  &::-webkit-scrollbar-thumb:hover {
    background: #555;
  }
`;

const StInfoLabel = styled.div`
  border-right: 1px solid gray;
  font-size: 1rem;
  font-weight: 600;
`;

const StInfoValue = styled.div`
  margin-left: 1rem;
`;

const StEditButtonRow = styled.div`
  display: flex;
  justify-content: flex-start;
  padding: 4px 8px;
`;

const StEditButton = styled(Button)`
  margin-top: 5px;
`;

const InfoPanelSubTitleWrapper = styled.div`
  display: flex;
  justify-content: space-between;
`;

const InfoPanelSubTitle = styled.div`
  font-size: 0.8rem;
  font-weight: 600;
  color: #888;
  text-transform: uppercase;
  padding: 9px 10px 2px;
`;

const EmptyDataMessage = styled.div`
  padding: 20px 10px;
  text-align: center;
  color: #999;
  font-size: 0.9rem;
  font-style: italic;
  border-bottom: 1px solid lightgray;
`;

export default SlideInfoPanel;